import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import {
  TrendingUp, Target, CheckCircle, AlertTriangle, Clock,
  Calendar, Users, FileText, Award, Zap, Filter, ArrowUpRight,
  MoreVertical, Play, Pause, Star
} from 'lucide-react';

const TasksDashboard = ({ stats, dashboardData }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('all');

  // Fetch tasks data
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        let url = 'https://greenyellow-nightingale-241896.hostingersite.com/api/task/tasks';

        if (token) {
          try {
            const decoded = jwtDecode(token);
            const role = decoded.role;
            const userId = decoded.id || decoded.userId || decoded._id;

            if (role && role !== 'company' && role !== 'Manager') {
              url = `https://greenyellow-nightingale-241896.hostingersite.com/api/task/tasks/employee/${userId}`;
            }
          } catch (e) {
            console.error('Error decoding token in TasksDashboard:', e);
          }
        }

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch tasks: ${response.status}`);
        }

        const data = await response.json();
        console.log('📋 Raw tasks data:', data);

        // Handle different response formats
        let tasksData = [];
        if (Array.isArray(data)) {
          tasksData = data;
        } else if (data.data && Array.isArray(data.data)) {
          tasksData = data.data;
        } else if (data.tasks && Array.isArray(data.tasks)) {
          tasksData = data.tasks;
        } else {
          tasksData = [];
        }

        // Filter out parent template tasks (which are just templates/blueprints)
        const activeTasks = tasksData.filter(task => {
          const isParentTemplate = task.repeat && !task.isRecurringInstance;
          return !isParentTemplate;
        });

        setTasks(activeTasks);
      } catch (err) {
        console.error('❌ Error fetching tasks:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  // Process tasks data for dashboard
  const processTasksData = () => {
    if (!tasks.length) return {};

    const now = new Date();
    const currentTasks = tasks.filter(task => {
      const taskDate = new Date(task.startDateTime || task.createdAt);
      switch (timeRange) {
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return taskDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return taskDate >= monthAgo;
        case 'two-months':
          const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
          return taskDate >= twoMonthsAgo;
        case 'quarter':
          const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          return taskDate >= quarterAgo;
        case 'all':
        default:
          return true;
      }
    });

    // Calculate metrics
    const statusCounts = {
      pending: currentTasks.filter(t => t.status === 'pending').length,
      'in-progress': currentTasks.filter(t => t.status === 'in-progress').length,
      completed: currentTasks.filter(t => t.status === 'completed').length,
      're-late-complete': currentTasks.filter(t => t.status === 're-late-complete').length,
    };

    const overdueTasks = currentTasks.filter(task =>
      task.endDateTime && new Date(task.endDateTime) < now &&
      !['completed', 're-late-complete'].includes(task.status)
    ).length;

    const totalTasks = currentTasks.length;
    const completedTasks = statusCounts.completed + statusCounts['re-late-complete'];
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    const totalCreditPoints = currentTasks.reduce((sum, task) => sum + (task.creditPoints || 0), 0);
    const completedCreditPoints = currentTasks
      .filter(task => ['completed', 're-late-complete'].includes(task.status))
      .reduce((sum, task) => sum + (task.creditPoints || 0), 0);

    // Priority distribution
    const priorityData = [
      { name: 'High', value: currentTasks.filter(t => t.priority === 'high').length, color: '#ef4444' },
      { name: 'Medium', value: currentTasks.filter(t => t.priority === 'medium').length, color: '#f59e0b' },
      { name: 'Low', value: currentTasks.filter(t => t.priority === 'low').length, color: '#10b981' }
    ].filter(item => item.value > 0);

    // Weekly progress data
    const weeklyProgress = getWeeklyProgress(currentTasks);

    // Recent tasks
    const recentTasks = [...currentTasks]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);

    // Top performers — prefer pre-resolved names from dashboardData.performanceData
    // Fall back to local ID-based calculation only if dashboardData is unavailable
    const topPerformers = (dashboardData?.performanceData && dashboardData.performanceData.length > 0)
      ? dashboardData.performanceData.slice(0, 3).map(emp => ({
        id: emp.name,
        name: emp.name,
        completionRate: emp.rate,
        points: emp.points,
        tasks: emp.totalTasks
      }))
      : getTopPerformers(currentTasks);

    return {
      totalTasks,
      completedTasks,
      completionRate,
      overdueTasks,
      statusCounts,
      priorityData,
      weeklyProgress,
      recentTasks,
      topPerformers,
      totalCreditPoints,
      completedCreditPoints,
      activeTasks: statusCounts.pending + statusCounts['in-progress']
    };
  };

  const getWeeklyProgress = (tasks) => {
    const weeks = [];
    const now = new Date();

    for (let i = 3; i >= 0; i--) {
      const startDate = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
      const endDate = new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000);

      const weekTasks = tasks.filter(task => {
        const taskDate = new Date(task.startDateTime || task.createdAt);
        return taskDate >= startDate && taskDate <= endDate;
      });

      const completed = weekTasks.filter(task =>
        ['completed', 're-late-complete'].includes(task.status)
      ).length;

      weeks.push({
        name: `W${i + 1}`,
        week: `Week ${i + 1}`,
        completed,
        total: weekTasks.length,
        rate: weekTasks.length > 0 ? Math.round(completed / weekTasks.length * 100) : 0
      });
    }

    return weeks;
  };

  const getTopPerformers = (tasks) => {
    // Group by assigned employee and calculate performance
    const employeePerformance = {};

    tasks.forEach(task => {
      if (task.assignedTo && task.assignedTo.length > 0) {
        const employeeId = task.assignedTo[0]; // Assuming first assigned employee
        if (!employeePerformance[employeeId]) {
          employeePerformance[employeeId] = {
            completed: 0,
            totalPoints: 0,
            tasks: 0
          };
        }

        employeePerformance[employeeId].tasks++;
        if (['completed', 're-late-complete'].includes(task.status)) {
          employeePerformance[employeeId].completed++;
          employeePerformance[employeeId].totalPoints += task.creditPoints || 0;
        }
      }
    });

    return Object.entries(employeePerformance)
      .map(([employeeId, data]) => ({
        id: employeeId,
        name: employeeId, // Fallback - real names come from dashboardData.performanceData
        completionRate: Math.round((data.completed / data.tasks) * 100),
        points: data.totalPoints,
        tasks: data.tasks
      }))
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 3);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const config = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-3 h-3" /> },
      'in-progress': { color: 'bg-blue-100 text-blue-800', icon: <Play className="w-3 h-3" /> },
      'completed': { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-3 h-3" /> },
      're-late-complete': { color: 'bg-orange-100 text-orange-800', icon: <AlertTriangle className="w-3 h-3" /> }
    };

    const { color, icon } = config[status] || config.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {icon}
        {status.replace('-', ' ')}
      </span>
    );
  };

  // Priority badge component
  const PriorityBadge = ({ priority }) => {
    const config = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config[priority]}`}>
        {priority}
      </span>
    );
  };

  // Metric Card Component
  const MetricCard = ({ title, value, subtitle, icon, trend, color }) => (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
      </div>
      {trend && (
        <div className="flex items-center mt-4 text-sm">
          <span className={`flex items-center ${trend.value > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend.value > 0 ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowUpRight className="w-4 h-4 mr-1 transform rotate-180" />}
            {trend.label}
          </span>
        </div>
      )}
    </div>
  );

  const tasksData = processTasksData();

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading tasks dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-red-800 mb-2">Error Loading Dashboard</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          Reload Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-5">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
          <p className="text-gray-600 mt-2">Monitor and analyze team task performance</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="all">All</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="two-months">Last 60 Days</option>
            <option value="quarter">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 p-4">
        <MetricCard
          title="Total Tasks"
          value={tasksData.totalTasks || 0}
          subtitle="All assigned tasks"
          icon={<FileText className="w-6 h-6 text-blue-600" />}
          color="bg-blue-50"
          trend={{ value: 12, label: "+12% from last month" }}
        />

        <MetricCard
          title="Completion Rate"
          value={`${tasksData.completionRate || 0}%`}
          subtitle={`${tasksData.completedTasks || 0} of ${tasksData.totalTasks || 0} tasks`}
          icon={<CheckCircle className="w-6 h-6 text-green-600" />}
          color="bg-green-50"
          trend={{ value: 8, label: "+8% improvement" }}
        />

        <MetricCard
          title="Active Tasks"
          value={tasksData.activeTasks || 0}
          subtitle="Currently in progress"
          icon={<Play className="w-6 h-6 text-orange-600" />}
          color="bg-orange-50"
          trend={{ value: -5, label: "5 less than last week" }}
        />

        <MetricCard
          title="Credit Points"
          value={tasksData.completedCreditPoints || 0}
          subtitle={`of ${tasksData.totalCreditPoints || 0} total`}
          icon={<Award className="w-6 h-6 text-purple-600" />}
          color="bg-purple-50"
          trend={{ value: 25, label: "+25 points earned" }}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
        {/* Completion Trend */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Weekly Completion Trend</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          {tasksData.weeklyProgress && tasksData.weeklyProgress.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={tasksData.weeklyProgress}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="completed"
                  name="Completed Tasks"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="total"
                  name="Total Tasks"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <Calendar className="w-12 h-12 mb-4 text-gray-300" />
              <p>No completion data available</p>
            </div>
          )}
        </div>

        {/* Priority Distribution */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Task Priority Distribution</h3>
            <AlertTriangle className="w-5 h-5 text-gray-400" />
          </div>
          {tasksData.priorityData && tasksData.priorityData.length > 0 ? (
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={tasksData.priorityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {tasksData.priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <Target className="w-12 h-12 mb-4 text-gray-300" />
              <p>No priority data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row - Recent Tasks & Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
        {/* Recent Tasks */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Tasks</h3>
            <Clock className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {tasksData.recentTasks && tasksData.recentTasks.length > 0 ? (
              tasksData.recentTasks.map((task) => (
                <div key={task._id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{task.title}</h4>
                    <p className="text-sm text-gray-500 truncate">{task.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <StatusBadge status={task.status} />
                      <PriorityBadge priority={task.priority} />
                      {task.creditPoints > 0 && (
                        <span className="text-xs text-purple-600 font-medium">
                          {task.creditPoints} pts
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <div>{new Date(task.endDateTime).toLocaleDateString()}</div>
                    {task.repeat && (
                      <div className="text-xs text-purple-600">Recurring</div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No recent tasks</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
            <Award className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {tasksData.topPerformers && tasksData.topPerformers.length > 0 ? (
              tasksData.topPerformers.map((performer, index) => (
                <div key={performer.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full text-white font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{performer.name}</h4>
                      <p className="text-sm text-gray-500">{performer.tasks} tasks</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{performer.completionRate}%</div>
                    <div className="text-sm text-gray-500">{performer.points} points</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No performance data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-xl p-6 text-white p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold">{tasksData.statusCounts?.pending || 0}</div>
            <div className="text-blue-200 text-sm">Pending</div>
          </div>
          <div>
            <div className="text-2xl font-bold">{tasksData.statusCounts?.['in-progress'] || 0}</div>
            <div className="text-blue-200 text-sm">In Progress</div>
          </div>
          <div>
            <div className="text-2xl font-bold">{tasksData.overdueTasks || 0}</div>
            <div className="text-blue-200 text-sm">Overdue</div>
          </div>
          <div>
            <div className="text-2xl font-bold">{tasksData.totalTasks || 0}</div>
            <div className="text-blue-200 text-sm">Total This Month</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksDashboard;
